import { useQuery } from '@tanstack/react-query';

import { patientInquiryApi } from '../api/patient-inquiry.adapter';
import type {
  ListPatientInquiriesParams,
  ListPatientInquiriesResult,
  PatientInquiryDetail,
} from '../types/patient-inquiry.types';

// Cache keys — `all` is the broad-invalidate root used by the reply mutation;
// `list(params)` keeps each keyword+sort+page combination independently cached;
// `detail(id)` is the slide-panel key (the reply mutation patches it directly).
export const patientInquiryQueryKeys = {
  all: ['patient-inquiries'] as const,
  list: (params: ListPatientInquiriesParams) =>
    [...patientInquiryQueryKeys.all, 'list', params] as const,
  detail: (id: number) => [...patientInquiryQueryKeys.all, 'detail', id] as const,
};

// Paginated list (PRD §3.1 / §3.2).
export function usePatientInquiriesQuery(params: ListPatientInquiriesParams) {
  return useQuery<ListPatientInquiriesResult>({
    queryKey: patientInquiryQueryKeys.list(params),
    queryFn: () => patientInquiryApi.listPatientInquiries(params),
    // Keep the prior page rendered while React Query refetches on search/sort/
    // page change — matches the prototype's smooth transition.
    placeholderData: (prev) => prev,
  });
}

// Single-inquiry detail (PRD §3.3 slide panel). The reply mutation patches this
// key on success so the thread re-renders without a round-trip.
export function usePatientInquiryQuery(id: number | null) {
  return useQuery<PatientInquiryDetail>({
    queryKey: patientInquiryQueryKeys.detail(id ?? -1),
    queryFn: () => patientInquiryApi.getPatientInquiry(id as number),
    enabled: id != null && id > 0,
  });
}
